// ========================================
// ERROR TYPE DEFINITIONS
// ========================================
// Standardized error types and categories for consistent error handling

export enum ErrorCategory {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  NETWORK = 'network',
  DATABASE = 'database',
  FIREBASE = 'firebase',
  USER_INPUT = 'user_input',
  SYSTEM = 'system',
  EXTERNAL_API = 'external_api',
  UI_RENDERING = 'ui_rendering',
  PERMISSION = 'permission',
  RATE_LIMIT = 'rate_limit',
  MAINTENANCE = 'maintenance',
  CHUNK_LOAD = 'chunk_load',
  STORAGE = 'storage',
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum ErrorRecoveryAction {
  NONE = 'none',
  RETRY = 'retry',
  RELOAD = 'reload',
  REDIRECT = 'redirect',
  LOGOUT = 'logout',
  CONTACT_SUPPORT = 'contact_support',
  MANUAL_INTERVENTION = 'manual_intervention',
}

export interface ErrorContext {
  userId?: string
  corporateEntityId?: string
  route?: string
  component?: string
  action?: string
  metadata?: Record<string, unknown>
  timestamp: string
  sessionId?: string
  userAgent?: string
  url?: string
  email?: string
  componentStack?: string
  eventId?: string
}

export interface StandardError {
  id: string
  code: string
  category: ErrorCategory
  severity: ErrorSeverity
  message: string
  userMessage: string
  technicalMessage?: string
  recoveryAction: ErrorRecoveryAction
  context: ErrorContext
  originalError?: Error
  stack?: string
  retryable: boolean
  retryCount?: number
  maxRetries?: number
}

export interface ErrorLogEntry extends StandardError {
  timestamp: string
  resolved: boolean
  resolvedAt?: string
  reportedToUser: boolean
  reportedToService: boolean
}

export interface ErrorRecoveryOptions {
  retryCount?: number
  maxRetries?: number
  retryDelayMs?: number
  exponentialBackoff?: boolean
  onRetry?: () => void
  onMaxRetriesReached?: () => void
  customAction?: () => void
  redirectUrl?: string
}

export interface ErrorHandlerConfig {
  enableConsoleLogging: boolean
  enableRemoteLogging: boolean
  enableUserNotifications: boolean
  defaultRetryCount: number
  defaultRetryDelayMs: number
  enableAutomaticRetry: boolean
  logToLocalStorage: boolean
  maxLocalStorageEntries: number
}

// Firebase-specific error codes mapping
export interface FirebaseErrorMap {
  code: string
  category: ErrorCategory
  severity: ErrorSeverity
  userMessage: string
  recoveryAction: ErrorRecoveryAction
  retryable: boolean
}

// Common Firebase error patterns
export const FIREBASE_ERROR_PATTERNS: Record<string, FirebaseErrorMap> = {
  // Authentication errors
  'auth/user-not-found': {
    code: 'auth/user-not-found',
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.MEDIUM,
    userMessage:
      'No account found with this email address. Please check your email or sign up for a new account.',
    recoveryAction: ErrorRecoveryAction.NONE,
    retryable: false,
  },
  'auth/wrong-password': {
    code: 'auth/wrong-password',
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.MEDIUM,
    userMessage: 'Incorrect password. Please try again or reset your password.',
    recoveryAction: ErrorRecoveryAction.NONE,
    retryable: false,
  },
  // Firebase answers both a wrong password and an unknown email with this
  // one code (email enumeration protection)
  'auth/invalid-credential': {
    code: 'auth/invalid-credential',
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.MEDIUM,
    userMessage:
      "That email and password don't match. Try again or reset your password.",
    recoveryAction: ErrorRecoveryAction.NONE,
    retryable: false,
  },
  'auth/invalid-email': {
    code: 'auth/invalid-email',
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    userMessage: 'Please enter a valid email address.',
    recoveryAction: ErrorRecoveryAction.NONE,
    retryable: false,
  },
  'auth/email-already-in-use': {
    code: 'auth/email-already-in-use',
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.MEDIUM,
    userMessage:
      'An account with this email already exists. Please sign in instead.',
    recoveryAction: ErrorRecoveryAction.REDIRECT,
    retryable: false,
  },
  'auth/weak-password': {
    code: 'auth/weak-password',
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    userMessage:
      'Password is too weak. Please choose a stronger password with at least 6 characters.',
    recoveryAction: ErrorRecoveryAction.NONE,
    retryable: false,
  },
  'auth/too-many-requests': {
    code: 'auth/too-many-requests',
    category: ErrorCategory.RATE_LIMIT,
    severity: ErrorSeverity.HIGH,
    userMessage:
      'Too many failed attempts. Please wait a few minutes before trying again.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },
  'auth/network-request-failed': {
    code: 'auth/network-request-failed',
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.HIGH,
    userMessage:
      'Network connection error. Please check your internet connection and try again.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },
  'auth/popup-blocked': {
    code: 'auth/popup-blocked',
    category: ErrorCategory.UI_RENDERING,
    severity: ErrorSeverity.MEDIUM,
    userMessage:
      'Popup was blocked by your browser. Please allow popups for this site and try again.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },
  'auth/popup-closed-by-user': {
    code: 'auth/popup-closed-by-user',
    category: ErrorCategory.USER_INPUT,
    severity: ErrorSeverity.LOW,
    userMessage: 'Sign-in was cancelled. Please try again to continue.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },

  // Firestore errors
  'firestore/permission-denied': {
    code: 'firestore/permission-denied',
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.HIGH,
    userMessage:
      "You don't have permission to access this resource. Please contact your administrator.",
    recoveryAction: ErrorRecoveryAction.CONTACT_SUPPORT,
    retryable: false,
  },
  'firestore/unavailable': {
    code: 'firestore/unavailable',
    category: ErrorCategory.DATABASE,
    severity: ErrorSeverity.HIGH,
    userMessage:
      'Database is temporarily unavailable. Please try again in a few moments.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },
  'firestore/deadline-exceeded': {
    code: 'firestore/deadline-exceeded',
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.MEDIUM,
    userMessage:
      'Request timed out. Please check your connection and try again.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },
  'firestore/resource-exhausted': {
    code: 'firestore/resource-exhausted',
    category: ErrorCategory.RATE_LIMIT,
    severity: ErrorSeverity.HIGH,
    userMessage:
      'Service is temporarily overloaded. Please try again in a few minutes.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },
  'firestore/not-found': {
    code: 'firestore/not-found',
    category: ErrorCategory.DATABASE,
    severity: ErrorSeverity.MEDIUM,
    userMessage: 'The requested resource was not found.',
    recoveryAction: ErrorRecoveryAction.NONE,
    retryable: false,
  },

  // Cloud Functions errors
  'functions/not-found': {
    code: 'functions/not-found',
    category: ErrorCategory.SYSTEM,
    severity: ErrorSeverity.HIGH,
    userMessage: 'Service unavailable. Please try again later.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },
  'functions/deadline-exceeded': {
    code: 'functions/deadline-exceeded',
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.MEDIUM,
    userMessage: 'Request timed out. Please try again.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },
  'functions/resource-exhausted': {
    code: 'functions/resource-exhausted',
    category: ErrorCategory.RATE_LIMIT,
    severity: ErrorSeverity.HIGH,
    userMessage: 'Service is busy. Please try again in a few minutes.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },
  'functions/unauthenticated': {
    code: 'functions/unauthenticated',
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.HIGH,
    userMessage: 'Please sign in to continue.',
    recoveryAction: ErrorRecoveryAction.LOGOUT,
    retryable: false,
  },
  'functions/permission-denied': {
    code: 'functions/permission-denied',
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.HIGH,
    userMessage: "You don't have permission to perform this action.",
    recoveryAction: ErrorRecoveryAction.CONTACT_SUPPORT,
    retryable: false,
  },

  // Storage errors
  'storage/unauthorized': {
    code: 'storage/unauthorized',
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.HIGH,
    userMessage: "You don't have permission to access this file.",
    recoveryAction: ErrorRecoveryAction.CONTACT_SUPPORT,
    retryable: false,
  },
  'storage/quota-exceeded': {
    code: 'storage/quota-exceeded',
    category: ErrorCategory.STORAGE,
    severity: ErrorSeverity.HIGH,
    userMessage: 'Storage quota exceeded. Please contact support.',
    recoveryAction: ErrorRecoveryAction.CONTACT_SUPPORT,
    retryable: false,
  },
  'storage/retry-limit-exceeded': {
    code: 'storage/retry-limit-exceeded',
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.HIGH,
    userMessage:
      'Upload failed after multiple attempts. Please try again later.',
    recoveryAction: ErrorRecoveryAction.RETRY,
    retryable: true,
  },
}

// Error patterns for different error types
export const ERROR_PATTERNS = {
  NETWORK_TIMEOUT: /timeout|network/i,
  PERMISSION_DENIED: /permission|unauthorized|forbidden/i,
  NOT_FOUND: /not found|404/i,
  RATE_LIMITED: /rate limit|too many/i,
  VALIDATION: /validation|invalid|required/i,
  CHUNK_LOAD: /loading chunk|chunk load/i,
  CORS: /cors|cross-origin/i,
} as const

export type ErrorPattern = keyof typeof ERROR_PATTERNS
